import { graphql } from "generated/gql";

export const CoreAccessionsListFieldsFragment = graphql(/* GraphQL */ `
  fragment AccessionsListFields on Accession {
    id
    accessionNumber
    accessionDate
    archived
    deaccessionDate
    scientificName
    updatedAt
    createdBy {
      id
      initials
      email
      givenName
      familyName
      avatarUrl
      isActivated
      accessRole
      deactivatedAt
    }
    updatedBy {
      id
      initials
      email
      givenName
      familyName
      avatarUrl
      isActivated
      accessRole
      deactivatedAt
    }
    materialType {
      name
    }
    tagsConnection {
      nodes {
        id
        name
      }
    }
    notes
    ipenNumber
    taxon {
      scientificName
      commonName
      fullyValidated
      nativeDistribution
      sharedScientificName {
        id
        scientificName
        status
        nameSourceUrl
        nameSource
        rank
        authorship
        classification {
          family
        }
        taxon {
          nativeDistribution
          climate
          globalRedListStatus
          lifeForms {
            nodes {
              id
              name
            }
          }
        }
      }
    }
    provenanceCategory {
      name
      codeId
    }
    provenanceCategoryCode
    donor
    createdByInitials
    plantMaterial(filters: $plantMaterialFilters) {
      status
      primaryImage {
        ...MaterialImageFields
      }
      images {
        ...MaterialImageFields
      }
    }
  }
`);

export const CorePlantMaterialListFieldsFragment = graphql(/* GraphQL */ `
  fragment MaterialsListFields on PlantMaterial {
    id
    qualifier
    status
    archived
    quantity
    massPlanting
    weight
    materialGroup
    createdAt
    firstPresent
    firstAbsent
    updatedAt
    sex
    tagNumber
    public
    lastObservation {
      id
      createdAt
      condition
    }
    createdBy {
      id
      initials
      email
      givenName
      familyName
      avatarUrl
      isActivated
      accessRole
      deactivatedAt
    }
    updatedBy {
      id
      initials
      email
      givenName
      familyName
      avatarUrl
      isActivated
      accessRole
      deactivatedAt
    }
    tagsConnection {
      nodes {
        id
        name
      }
    }
    notes
    condition
    lastObserved
    daysOverdueFollowUp
    followUp {
      count
      period
    }
    primaryImage {
      ...MaterialImageFields
    }
    images {
      ...MaterialImageFields
    }
    collectionSiteLocation {
      id
      name
      description
      archived
    }
    accession {
      accessionNumber
      scientificName
      donor
      taxon {
        scientificName
        commonName
        fullyValidated
        nativeDistribution
        sharedScientificName {
          id
          scientificName
          status
          nameSourceUrl
          nameSource
          rank
          authorship
          classification {
            family
          }
          taxon {
            nativeDistribution
            climate
            globalRedListStatus
            lifeForms {
              nodes {
                id
                name
              }
            }
          }
        }
      }
      provenanceCategory {
        name
        codeId
      }
      provenanceCategoryCode
      donor
    }
  }
`);

export const CorePageInfoFieldsFragment = graphql(/* GraphQL */ `
  fragment PageInfoFields on PageInfo {
    startCursor
    hasNextPage
    hasPreviousPage
    endCursor
  }
`);

export const getAccessionsDocument = graphql(/* GraphQL */ `
  query GetAccessions(
    $before: String
    $after: String
    $first: Int
    $last: Int
    $organisationSubdomain: String!
    $collectionSiteSlug: String!
    $orderBy: AccessionsOrderByInput
    $searchTerm: AccessionsSearchTerm
    $filters: AccessionFilter
    $includeArchived: Boolean
    $plantMaterialFilters: PlantMaterialFilter
  ) {
    org: organisationBySubdomain(subdomain: $organisationSubdomain) {
      site: collectionSiteBySlug(slug: $collectionSiteSlug) {
        total: accessionTotal(
          searchTerm: $searchTerm
          filters: $filters
          includeArchived: $includeArchived
        )
        result: accessions(
          before: $before
          after: $after
          first: $first
          last: $last
          orderBy: $orderBy
          searchTerm: $searchTerm
          filters: $filters
          includeArchived: $includeArchived
        ) {
          nodes {
            ...AccessionsListFields
          }
          pageInfo {
            ...PageInfoFields
          }
        }
      }
    }
  }
`);
export const getAccessionByIdDocument = graphql(`
  query GetAccessionById($id: ID!, $filters: PlantMaterialFilter) {
    accession(id: $id) {
      ...AccessionFields
    }
  }
`);
export const getPlantMaterialsDocument = graphql(/* GraphQL */ `
  query PlantMaterials(
    $organisationSubdomain: String!
    $collectionSiteSlug: String!
    $orderBy: PlantMaterialsOrderByInput
    $before: String
    $after: String
    $first: Int
    $last: Int
    $filters: PlantMaterialFilter
    $searchTerm: PlantMaterialsSearchTerm
    $includeArchived: Boolean
  ) {
    org: organisationBySubdomain(subdomain: $organisationSubdomain) {
      site: collectionSiteBySlug(slug: $collectionSiteSlug) {
        total: plantMaterialTotal(
          filters: $filters
          searchTerm: $searchTerm
          includeArchived: $includeArchived
        )
        result: plantMaterials(
          before: $before
          after: $after
          first: $first
          last: $last
          orderBy: $orderBy
          filters: $filters
          searchTerm: $searchTerm
          includeArchived: $includeArchived
        ) {
          nodes {
            ...MaterialsListFields
          }
          pageInfo {
            ...PageInfoFields
          }
        }
      }
    }
  }
`);

export const getMeGardenDocument = graphql(/* GraphQL */ `
  query GetMeGarden {
    me {
      collectionSites {
        name
        urlSlug
      }
    }
  }
`);

export const getSiteTaxaDocument = graphql(/* GraphQL */ `
  query GetSiteTaxa(
    $before: String
    $after: String
    $first: Int
    $last: Int
    $organisationSubdomain: String!
    $collectionSiteSlug: String!
    $searchTerm: TaxaSearchTerm
    $orderBy: TaxaOrderByInput
    $filter: TaxaFilter
  ) {
    org: organisationBySubdomain(subdomain: $organisationSubdomain) {
      site: collectionSiteBySlug(slug: $collectionSiteSlug) {
        total: taxaTotal(searchTerm: $searchTerm, filter: $filter)
        result: taxa(
          before: $before
          after: $after
          first: $first
          last: $last
          searchTerm: $searchTerm
          orderBy: $orderBy
          filter: $filter
        ) {
          nodes {
            ...TaxaListFields
          }
          pageInfo {
            ...PageInfoFields
          }
        }
      }
    }
  }
`);
